import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, Users, MapPin, Sparkles, ArrowRight,
  ShieldCheck, BadgeCheck, Headphones, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OCCASIONS } from "@/lib/api";

const HERO_SLIDES = [
  "https://images.pexels.com/photos/33575697/pexels-photo-33575697.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2400",
  "https://images.unsplash.com/photo-1582610116397-edb318620f90?auto=format&fit=crop&q=85&w=2400",
  "https://images.unsplash.com/photo-1571508601891-ca5e7a713859?auto=format&fit=crop&q=85&w=2400",
  "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&q=85&w=2400",
];

const TRUST = [
  { icon: BadgeCheck, label: "Verified stays" },
  { icon: Headphones, label: "30-min host desk" },
  { icon: ShieldCheck, label: "Managed premium" },
];

const STAT_CARDS = [
  { n: "30+", l: "Curated stays", slug: "stays" },
  { n: "15+", l: "Hosted events", slug: "events" },
  { n: "1,000+", l: "Celebrations", slug: "celebrations" },
];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    location: "Hyderabad",
    check_in: "",
    check_out: "",
    guests: "10",
    occasion: OCCASIONS[5],
  });

  useEffect(() => {
    const i = setInterval(() => setIdx((n) => (n + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(i);
  }, []);

  const search = () => {
    const params = new URLSearchParams();
    if (form.guests) params.set("guests", form.guests);
    if (form.occasion) params.set("occasion", form.occasion);
    navigate(`/stays?${params.toString()}`);
  };

  return (
    <section className="relative w-full overflow-hidden" data-testid="hero-section">
      <div className="relative w-full min-h-[860px] lg:min-h-[920px]">
        {/* Background */}
        {HERO_SLIDES.map((src, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-[1500ms] ease-out"
            style={{ opacity: i === idx ? 1 : 0 }}
          >
            <img src={src} alt="" className="img-cover" />
          </div>
        ))}
        <div className="absolute inset-0 hero-overlay" />

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 md:pt-20 pb-16 text-[#FAFAF8]">
          <div className="kicker text-[#F4A930] mb-4 flex items-center gap-2 fade-up">
            <Sparkles size={14} /> Premium farmhouse booking · Hyderabad
          </div>
          <h1 className="font-sora text-4xl sm:text-5xl lg:text-[4.4rem] font-semibold tracking-tight leading-[1.02] max-w-4xl fade-up">
            Find the perfect farmhouse
            <br />
            for every <span className="text-[#7CB342]">celebration.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-[#FAFAF8]/85 text-base md:text-lg leading-relaxed fade-up">
            Curated private stays around Hyderabad for birthdays, weddings, corporate offsites and unforgettable weekends.
          </p>

          {/* MAIN — Booking widget. This is the primary visual focus. */}
          <div
            className="mt-10 lg:mt-14 bg-white text-[#0E2E24] rounded-[24px] p-5 lg:p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] border border-white/40 fade-up"
            data-testid="hero-search-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="kicker text-[#1F5A43]">Book a stay</div>
              <div className="hidden md:flex items-center gap-1.5 text-xs text-[#5B6B63] font-sora">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#7CB342]" />
                Instant confirmation by host desk in 30 min
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-stretch">
              <SearchField icon={<MapPin size={15} />} label="Where" className="col-span-2 lg:col-span-2">
                <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                  <SelectTrigger className="border-0 bg-transparent p-0 h-auto text-base font-sora font-semibold text-[#0E2E24] focus:ring-0 shadow-none" data-testid="hero-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                    <SelectItem value="Bengaluru">Bengaluru (soon)</SelectItem>
                    <SelectItem value="Goa">Goa (soon)</SelectItem>
                  </SelectContent>
                </Select>
              </SearchField>
              <SearchField icon={<CalendarDays size={15} />} label="Check in" className="col-span-1 lg:col-span-2">
                <Input type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                  className="border-0 bg-transparent p-0 h-auto text-base font-sora font-semibold text-[#0E2E24] focus-visible:ring-0 shadow-none"
                  data-testid="hero-check-in" />
              </SearchField>
              <SearchField icon={<CalendarDays size={15} />} label="Check out" className="col-span-1 lg:col-span-2">
                <Input type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                  className="border-0 bg-transparent p-0 h-auto text-base font-sora font-semibold text-[#0E2E24] focus-visible:ring-0 shadow-none"
                  data-testid="hero-check-out" />
              </SearchField>
              <SearchField icon={<Users size={15} />} label="Guests" className="col-span-1 lg:col-span-2">
                <Input type="number" min={1} value={form.guests}
                  onChange={(e) => setForm({ ...form, guests: e.target.value })}
                  className="border-0 bg-transparent p-0 h-auto text-base font-sora font-semibold text-[#0E2E24] focus-visible:ring-0 shadow-none"
                  data-testid="hero-guests" />
              </SearchField>
              <SearchField icon={<Sparkles size={15} />} label="Occasion" className="col-span-1 lg:col-span-2">
                <Select value={form.occasion} onValueChange={(v) => setForm({ ...form, occasion: v })}>
                  <SelectTrigger className="border-0 bg-transparent p-0 h-auto text-base font-sora font-semibold text-[#0E2E24] focus:ring-0 shadow-none" data-testid="hero-occasion">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </SearchField>
              <div className="col-span-2 lg:col-span-2 flex items-stretch">
                <Button
                  onClick={search}
                  className="w-full bg-[#0E2E24] hover:bg-[#1F5A43] text-[#FAFAF8] rounded-2xl py-7 font-sora font-semibold text-base gap-2"
                  data-testid="hero-widget-search-btn"
                >
                  <Search size={18} /> Search
                </Button>
              </div>
            </div>

            {/* Trust + secondary CTAs INSIDE the booking card */}
            <div className="mt-5 pt-5 border-t border-[#0E2E24]/10 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-sora text-[#5B6B63]">
                {TRUST.map((t) => (
                  <span key={t.label} className="flex items-center gap-1.5">
                    <t.icon size={15} className="text-[#7CB342]" /> {t.label}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => navigate("/stays?managed=true")} className="text-sm font-sora font-medium text-[#0E2E24] hover:text-[#1F5A43] underline-offset-4 hover:underline" data-testid="hero-explore-stays-btn">
                  Explore Premium Stays →
                </button>
                <span className="text-[#0E2E24]/20">|</span>
                <button onClick={() => navigate("/events")} className="text-sm font-sora font-medium text-[#0E2E24] hover:text-[#1F5A43] underline-offset-4 hover:underline" data-testid="hero-book-event-btn">
                  Host Your Event →
                </button>
              </div>
            </div>
          </div>

          {/* Floating stat cards row */}
          <div className="mt-7 grid grid-cols-3 gap-3 max-w-2xl">
            {STAT_CARDS.map((s) => (
              <div
                key={s.l}
                className="bg-[#FAFAF8]/15 backdrop-blur-md border border-[#FAFAF8]/25 rounded-2xl px-4 py-3.5 text-[#FAFAF8]"
                data-testid={`hero-stat-${s.slug}`}
              >
                <div className="font-sora font-semibold text-2xl leading-none">{s.n}</div>
                <div className="mt-1 text-xs text-[#FAFAF8]/80 font-sora">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Slide indicators */}
          <div className="mt-8 flex items-center gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1 rounded-full transition-all ${i === idx ? "w-10 bg-[#F4A930]" : "w-5 bg-[#FAFAF8]/40"}`}
                aria-label={`Slide ${i + 1}`}
                data-testid={`hero-slide-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SearchField({ icon, label, children, className = "" }) {
  return (
    <div className={`px-4 py-3 rounded-2xl bg-[#FAFAF8] hover:bg-[#E8F2EC]/60 transition-colors ${className}`}>
      <div className="flex items-center gap-1.5 text-[#1F5A43] text-[10px] tracking-[0.22em] uppercase font-semibold font-sora mb-1.5">
        {icon} {label}
      </div>
      {children}
    </div>
  );
}
